import Stripe from "stripe";
import { assertProviderEnabled } from "@/modules/legal/providers/assertProviderEnabled";
import { topupAmounts } from "@/framework/billing/topup/topupAmounts";

// Lazy-initialised singleton so the module can be imported at build time
// without requiring the env var to be present.
// The CJS .d.ts declares Stripe as a plain function, but the runtime
// implementation is a class that must be called with `new`. We cast
// through `any` to reconcile the two.
const StripeClass = Stripe as any as new (key: string) => Stripe.Stripe;

let _stripe: Stripe.Stripe | null = null;

export function getStripe(): Stripe.Stripe {
  assertProviderEnabled("stripe");
  if (!_stripe) {
    const key = process.env.STRIPE_SECRET_KEY;
    if (!key) throw new Error("STRIPE_SECRET_KEY is not set");
    _stripe = new StripeClass(key);
  }
  return _stripe;
}

// Quick-select suggestions shown as buttons; users can also type a custom amount.
export const suggestedTopupAmounts = topupAmounts;

export type BuyerType = "business" | "private";

type StripeCustomerSummary = {
  id: string;
  name?: string | null;
  deleted?: boolean;
  address?: {
    line1?: string | null;
    line2?: string | null;
    city?: string | null;
    postal_code?: string | null;
    country?: string | null;
  } | null;
};

// Finds an existing Stripe Customer by email, or creates one.
// Reusing the same customer lets Stripe persist tax IDs and
// invoice history across multiple purchases.
export async function findOrCreateStripeCustomer(
  email: string,
): Promise<string> {
  const existingCustomer = await findStripeCustomerByEmail(email);
  if (existingCustomer) {
    return existingCustomer.id;
  }
  const stripe = getStripe();
  const customer = await stripe.customers.create({ email });
  return customer.id;
}

// Returns the existing Stripe Customer for an email, if any.
export async function findStripeCustomerByEmail(
  email: string,
): Promise<StripeCustomerSummary | null> {
  const stripe = getStripe();
  const existing = await stripe.customers.list({ email, limit: 1 });
  if (existing.data.length > 0) {
    const customer = existing.data[0] as StripeCustomerSummary;
    if (!customer.deleted) {
      return customer;
    }
  }
  return null;
}

// Maps ISO country codes to VIES VAT ID prefixes.
// Most are identical except Greece (GR -> EL).
const vatPrefixForCountry: Record<string, string> = { GR: "EL" };

export function getVatPrefix(countryCode: string): string {
  return vatPrefixForCountry[countryCode] ?? countryCode;
}

// Converts a whole-credit amount to microcredit (1 credit = 1_000_000).
export function eurToMicrocredit(credits: number): number {
  return credits * 1_000_000;
}
